import { forwardRef } from 'react';
import Icon from './Icon';

export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  leftIcon?: React.ReactNode;
  className?: string;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  value,
  onChange,
  options,
  placeholder,
  required = false,
  disabled = false,
  error = false,
  leftIcon,
  className = '',
  id,
  name,
  ...ariaProps
}, ref) => {
  const baseClasses = `
    w-full px-3 py-2 pr-8 border rounded-md bg-bg-primary text-text-primary 
    focus:border-action-key-default focus:ring-1 focus:ring-action-key-default focus:outline-none
    disabled:bg-bg-disabled disabled:text-text-disabled disabled:cursor-not-allowed
    appearance-none
  `;
  
  const errorClasses = error 
    ? 'border-danger-border focus:border-danger focus:ring-danger' 
    : 'border-border-light';
    
  const leftIconClasses = leftIcon ? 'pl-10' : '';

  return (
    <div className="relative">
      {leftIcon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <div className="text-text-secondary">
            {leftIcon}
          </div>
        </div>
      )}
      
      <select
        ref={ref}
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className={`${baseClasses} ${errorClasses} ${leftIconClasses} ${className}`}
        {...ariaProps}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option 
            key={option.value} 
            value={option.value}
            disabled={option.disabled}
          >
            {option.icon ? `${option.icon} ${option.label}` : option.label}
          </option>
        ))}
      </select>
      
      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <Icon name="chevron-down" size="sm" color="secondary" />
      </div>
    </div>
  );
});

Select.displayName = 'Select';

export default Select;